export interface ParsedIngredient {
  qty: number | null;
  unit: string;
  name: string;
}

const UNITS = new Set([
  "g",
  "kg",
  "mg",
  "oz",
  "lb",
  "lbs",
  "ml",
  "l",
  "dl",
  "cup",
  "cups",
  "tbsp",
  "tsp",
  "tablespoon",
  "tablespoons",
  "teaspoon",
  "teaspoons",
  "pinch",
  "dash",
  "piece",
  "pieces",
  "slice",
  "slices",
  "can",
  "cans",
  "clove",
  "cloves",
  "stalk",
  "stalks",
  "bunch",
  "bunches",
  "sprig",
  "sprigs",
  "head",
  "heads",
  "ball",
  "balls",
  "sheet",
  "sheets",
  "package",
  "pkg",
]);

const FRACTION_MAP: Record<string, number> = {
  "¼": 0.25,
  "½": 0.5,
  "¾": 0.75,
  "⅓": 1 / 3,
  "⅔": 2 / 3,
  "⅛": 0.125,
  "⅜": 0.375,
  "⅝": 0.625,
  "⅞": 0.875,
};

function parseFraction(s: string): number | null {
  if (FRACTION_MAP[s] !== undefined) return FRACTION_MAP[s];
  const slashMatch = s.match(/^(\d+)\/(\d+)$/);
  if (slashMatch) {
    const denom = Number(slashMatch[2]);
    if (denom === 0) return null;
    return Number(slashMatch[1]) / denom;
  }
  return null;
}

function parseQty(s: string): number | null {
  const n = Number(s);
  if (!Number.isNaN(n) && n > 0) return n;
  return parseFraction(s);
}

// "1.5 lb beef shank" → { qty: 1.5, unit: "lb", name: "beef shank" }
// "1/3 cup roasted peanuts" → { qty: 0.333, unit: "cup", name: "roasted peanuts" }
// "200 g 00 flour" → { qty: 200, unit: "g", name: "00 flour" }
// "starter — 100g" → { qty: 100, unit: "g", name: "starter" }
// "juice of 1 lemon" → { qty: null, unit: "", name: "juice of 1 lemon" }
export function parseIngredient(text: string): ParsedIngredient {
  const trimmed = text.trim();
  if (!trimmed) return { qty: null, unit: "", name: "" };

  // Try trailing "name — 100g" / "name - 100g" pattern first
  const trailingMatch = trimmed.match(/^(.+?)\s*[—–\-]\s*(\d+(?:\.\d+)?)\s*([a-zA-Z]+)$/);
  if (trailingMatch) {
    const qty = Number(trailingMatch[2]);
    const unit = trailingMatch[3].toLowerCase();
    if (!Number.isNaN(qty) && qty > 0) {
      return { qty, unit, name: trailingMatch[1].trim() };
    }
  }

  const tokens = trimmed.split(/\s+/);
  if (tokens.length === 0) return { qty: null, unit: "", name: trimmed };

  // Handle stuck-together "400g" / "1.5kg" / "1/2lb" / "½cup" — split number from unit
  const stuckMatch = tokens[0].match(/^(\d+(?:\.\d+)?|[\d]+\/[\d]+|[¼½¾⅓⅔⅛⅜⅝⅞])\s*([a-zA-Z]+)$/);
  if (stuckMatch && UNITS.has(stuckMatch[2].toLowerCase())) {
    const qty = parseQty(stuckMatch[1]);
    if (qty !== null) {
      const unit = stuckMatch[2].toLowerCase();
      const name = tokens
        .slice(1)
        .join(" ")
        .replace(/^[,;:\s]+/, "");
      return { qty, unit, name: name || trimmed };
    }
  }

  const firstQty = parseQty(tokens[0]);
  if (firstQty === null) return { qty: null, unit: "", name: trimmed };

  // Check for mixed number: "1 1/2 cups water" → whole + fraction
  let qty = firstQty;
  let consumed = 1;

  if (tokens.length > 1) {
    const secondQty = parseFraction(tokens[1]);
    if (secondQty !== null && firstQty === Math.floor(firstQty)) {
      qty = firstQty + secondQty;
      consumed = 2;
    }
  }

  // Check if next token is a unit
  if (consumed < tokens.length) {
    const candidate = tokens[consumed].toLowerCase().replace(/[.,;]$/, "");
    if (UNITS.has(candidate)) {
      const unit = candidate;
      const name = tokens
        .slice(consumed + 1)
        .join(" ")
        .replace(/^[,;:\s]+/, "");
      return { qty, unit, name: name || trimmed };
    }
  }

  // No unit found — qty is present but unit-less ("2 star anise", "8 dried red chilies")
  const name = tokens
    .slice(consumed)
    .join(" ")
    .replace(/^[,;:\s]+/, "");
  return { qty, unit: "", name: name || trimmed };
}

export function formatQty(qty: number, multiplier: number): string {
  const v = qty * multiplier;
  if (v >= 100) return Math.round(v).toString();
  if (v >= 10) return (Math.round(v * 10) / 10).toString();
  const rounded = Math.round(v * 100) / 100;
  return rounded.toString();
}
